import React, { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { Search, Building2, Plus, X, Loader2, Users } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useApiAuth } from '../../../contexts/ApiAuthContext';
import { clientService } from '../api/clientsApi';
import { BRAND_COLORS, SYSTEM_COLORS } from '../../../constants/brandPalette';
import { ClientCard, AddClientModal, ClientDetailModal } from '../components';

const ClientsPage = () => {
  const { user, hasPermission } = useApiAuth();

  const canView   = hasPermission('clients.view');
  const canManage = user?.role === 'admin' || user?.role === 'supervisor';

  const [clients, setClients]               = useState([]);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState(null);
  const [search, setSearch]                 = useState('');
  const [statusFilter, setStatusFilter]     = useState('all');
  const [selectedClient, setSelectedClient] = useState(null);
  const [showAdd, setShowAdd]               = useState(false);

  const load = useCallback(async () => {
    if (!user || !canView) return;
    setLoading(true);
    setError(null);
    try {
      setClients(await clientService.getAllClients());
    } catch {
      setError('Failed to load clients');
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  }, [user, canView]);

  useEffect(() => { load(); }, [load]);

  const handleUpdated = (updated) => {
    setClients(prev => prev.map(c => c.id === updated.id ? { ...c, ...updated } : c));
    setSelectedClient(prev => prev?.id === updated.id ? { ...prev, ...updated } : prev);
  };

  const handleDeleted = (id) => setClients(prev => prev.filter(c => c.id !== id));
  const handleCreated = (created) => setClients(prev => [created, ...prev]);

  const filtered = clients.filter(c => {
    const q = search.toLowerCase();
    const matchSearch = !q ||
      c.organization?.toLowerCase().includes(q) ||
      c.name?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q) ||
      c.industry?.toLowerCase().includes(q);
    return matchSearch && (statusFilter === 'all' || c.status === statusFilter);
  });

  if (!canView) return <Navigate to="/unauthorized" replace />;

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">Clients</h1>
          <p className="text-sm text-gray-500 mt-0.5">External client directory · {clients.length} total</p>
        </div>
        {canManage && (
          <button
            onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90"
            style={{ backgroundColor: BRAND_COLORS.primary }}
          >
            <Plus size={15} /> Add Client
          </button>
        )}
      </div>

      {/* Stats */}
      {!loading && clients.length > 0 && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { label: 'Total',    value: clients.length,                                                              color: BRAND_COLORS.primary },
            { label: 'Active',   value: clients.filter(c => c.status === 'active').length,                          color: SYSTEM_COLORS.success },
            { label: 'Inactive', value: clients.filter(c => c.status === 'inactive').length,                        color: SYSTEM_COLORS.textSoft },
            { label: 'Projects', value: clients.reduce((s, c) => s + (c.project_ids?.length ?? 0), 0),              color: SYSTEM_COLORS.info },
          ].map(({ label, value, color }) => (
            <div key={label} className="px-4 py-3 border rounded-xl"
              style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}>
              <p className="text-xl font-bold sm:text-2xl" style={{ color }}>{value}</p>
              <p className="text-xs text-gray-500 mt-0.5">{label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="flex items-center flex-1 gap-2 rounded-xl border px-3 py-2.5"
          style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}>
          <Search size={15} className="flex-shrink-0 text-gray-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, org, or industry…"
            className="flex-1 text-sm text-gray-800 bg-transparent outline-none"
          />
          {search && (
            <button onClick={() => setSearch('')}>
              <X size={13} className="text-gray-400 hover:text-gray-600" />
            </button>
          )}
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="sm:w-40 px-3 py-2.5 rounded-xl border text-sm outline-none transition-all focus:ring-2 focus:ring-[#37559e]/20 focus:border-[#37559e]"
          style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}
        >
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="pending">Pending</option>
        </select>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center gap-3 py-16">
          <Loader2 size={20} className="animate-spin" style={{ color: BRAND_COLORS.primary }} />
          <span className="text-sm text-gray-500">Loading clients…</span>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center gap-3 py-16">
          <p className="text-sm text-red-500">{error}</p>
          <button onClick={load} className="px-4 py-2 text-sm font-medium text-white rounded-xl"
            style={{ backgroundColor: BRAND_COLORS.primary }}>Retry</button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 py-16">
          <div className="flex items-center justify-center w-14 h-14 rounded-2xl"
            style={{ backgroundColor: SYSTEM_COLORS.shellSurfaceMuted }}>
            <Users size={24} style={{ color: BRAND_COLORS.primary }} />
          </div>
          <p className="text-sm font-medium text-gray-700">
            {search ? 'No clients match your search' : 'No clients yet'}
          </p>
          <p className="text-xs text-gray-400">
            {search ? 'Try a different query' : canManage ? 'Click "Add Client" to get started' : 'No client profiles have been created'}
          </p>
        </div>
      ) : (
        <>
          <p className="text-xs text-gray-400">Showing {filtered.length} of {clients.length}</p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {filtered.map(c => (
              <ClientCard key={c.id} client={c} onClick={() => setSelectedClient(c)} />
            ))}
          </div>
        </>
      )}

      {/* Modals */}
      {selectedClient && (
        <ClientDetailModal
          client={selectedClient}
          canManage={canManage}
          onClose={() => setSelectedClient(null)}
          onUpdated={handleUpdated}
          onDeleted={handleDeleted}
        />
      )}

      <AddClientModal
        isOpen={showAdd}
        onClose={() => setShowAdd(false)}
        onCreated={handleCreated}
      />
    </div>
  );
};

export default ClientsPage;
